import { useSEO } from '../hooks/useSEO';

function Home() {
    useSEO();
    return (
        <div className="main-content home">
            <a href="https://www.<i>AmeriQuests</i>.org">
                <img className="ameriquestlogo" src="AmeriQuests.svg"></img>
            </a>
            <h1>Unhoused America</h1>
            
            <h2>Call for Submissions</h2>

            <h3>Overview</h3>

            <p>
                <i>AmeriQuests</i> is looking for text submissions for peer review, commentary, and creative works for publishing in an upcoming special edition, <i>Unhoused America</i>.
            </p>
            
            <p>
                <i>Unhoused America</i> seeks to unsettle persistent, and often counterproductive, stereotypes about homelessness in the United States by curating a multimedia online issue of <i>AmeriQuests</i>—developed in collaboration with MIT’s Knowledge Futures Group. The project demonstrates diverse experiences of homelessness, multi-faceted, varying by region, demographic characteristics, and lived experience. Our approach leverages geospatial mapping, a historical timeline, and AI-generated, trauma-responsive storytelling to promote more nuanced, data-rich, and empathetic understandings of unhoused populations while offering planners, policymakers, social service providers, and designers greater legibility of unhoused community needs.
            </p>
            
            <h3>Core Components</h3>
            <ul>
                <li>
                    <b>Interactive Geospatial Map:</b> Using HUD Continuum of Care regions and 2024 point-in-time county data, <i>Unhoused America</i> will map homelessness across the US, disaggregated by demographics to identify regional “hot spots” and thematic disparities often overlooked in other publicly available data.
                </li>
                <li>
                    <b>Timeline:</b> This feature situates contemporary homelessness within its broad historical, legal, and policy context, deepening visitors' understanding of its causes and consequences.  
                </li>
                <li>
                    <b>Embodicons (AI-Generated Profiles):</b> To approach storytelling in a trauma-responsive, non-extractive manner, <i>Unhoused America</i> synthesizes existing media and documentation of unhoused experiences to create up to five million AI-based, composite profiles that illustrate typical day-to-day experiences of unhoused individuals, including factors contributing to their homelessness—preserving dignity and avoiding overburdening vulnerable voices, while humanizing the experience. Targeted recommendations are also made for service providers within the designated metropolitan area, based on the Embodicons' unique risk factors and needs. 
                </li>
            </ul>
            
            <h3>Text Submissions for Peer-Review</h3>
            <p>
                Text submissions consisting of a 2,000 to 5,000-word manuscript with accompanying figures or images (visuals are encouraged) that directly engage perceptions of homelessness. We do welcome submissions that directly work in conversation with the platform’s interactive geospatial map, timeline, and Embodicons. Accepted submissions will undergo a double peer review process.
            </p>
            
            <h3>Text Submissions as Commentary</h3>
            <p>
                Text submissions consisting of a 1,000 to 2,500-word manuscript with accompanying figures or images (visuals are encouraged) that directly engage perceptions of homelessness. We do welcome submissions that directly work in conversation with the platform’s interactive geospatial map, timeline, and Embodicons. Accepted submissions will not undergo a peer review process.
            </p>
            
            <h3>Creative Submissions</h3>
            <p>
                Creative works can include media submissions (project portfolios: visual art, performance art, design, architecture, culinary arts, among other media practices) consisting of up to 10 separate visual, audio, or video files, accompanied by textual descriptions and captions. Submissions should be accompanied by a 500- to 1,000-word text providing commentary on the work. Text is strongly encouraged for creative submissions to be considered for publication.
            </p>
            
            <h2>Submission Guidelines </h2>
            
            <h3>Editorial Review and Submission Process</h3>
            <p>
                For text submissions (peer review and commentary), please discern upon submission if you intend to for peer review or not. Upon preliminary acceptance, peer-reviewed text submissions will undergo a double peer-review process. Commentary text submissions and creative submissions are not peer reviewed and will be selected at the discretion of the editorial team.  
            </p>
            
            <h3>Manuscript Preparation </h3>
            <p>
                As this is an online publication, the range of possible references, hyperlinks, and internal references is as broad as the technology will support. However, expect that all citations are consistent with a singular style of language.
            </p>
            
            <p>
                Contributors should send only proofread manuscripts, with correct punctuation, spelling, and citations, and images that are appropriately captioned and cited. All text documents must be written in a 12-point (size) Times New Roman double-spaced font.
            </p>  
            
            <p>
                If you are sending a manuscript that includes figures or images, please send each as a separate file, NOT embedded within the text document. Each visual item should be numbered, titled, and have its placement indicated within the text. Please see below for specifics on image format requirements. 
            </p>
            
            <h3>Text Submission Guidelines</h3>
            
            <p>
                All text documents should be submitted as Microsoft Office Document (.docx) files via email to Dana McKinney White (<a href="mailto:mckinneywhite@gsd.harvard.edu">mckinneywhite@gsd.harvard.edu</a>), cc’ing Robert Barsky (<a href="mailto:robert.barsky@vanderbilt.edu">robert.barsky@vanderbilt.edu</a>). Figures or images should be attached as individual, high-resolution JPG or TIFF files. When submitting, please specify whether the piece is a text submission for peer review or as a commentary.  
            </p>
            
            <p>
                All sources should be properly cited within the text using a consistent style of language (MLA, Chicago, APA, ABA, etc.). Please include a bibliography with your manuscript, unless you use endnotes or footnotes, in which case a separate list of works cited is not needed. Image captions should be included as a separate Microsoft Office Document (.docx) file.
            </p>
            
            <h4>Copyright Permissions</h4>
            <p>
                If your work contains borrowed images or other elements that are not of your own authorship, you must obtain the proper copyright permissions. All necessary permissions must be obtained to use copyrighted imagery, such as photographs, illustrations, tables, or drawings.  
              </p>
            
            <p>
                All letters of permission should accompany your manuscript in the initial submission. <i>AmeriQuests</i> will not subsidize the cost of attaining permissions for submitted work.  
            </p>
            
            <h4>Collaboration</h4>
            <p>
                If more than one author has collaborated on a piece of work, each should have the opportunity to view the work before submission and throughout the editorial process. Please designate one person to handle correspondence, but include all contributors’ names and emails upon submission, and ensure that all parties remain informed of editorial decisions.  
            </p>
            
            <h4>AI/LLM Issues</h4>
            <p>
                No LLM tool can be credited as an author on any submission. Any substantive use of LLM text-based or visual-based tools must be appropriately cited.
            </p>
            
            <h4>Writers' Fees</h4>
            <p>
                Writers of editorial content shall not be paid for their work unless the work is especially commissioned for the Journal under special arrangement and under a separate contract.  
            </p>
            
            <h3>Creative Submission Guidelines</h3>
            
            <p>
                All images should be submitted as individual, high-resolution JPEG, PNG, or GIF (animated only) files via email to Dana McKinney White (<a href="mailto:mckinneywhite@gsd.harvard.edu">mckinneywhite@gsd.harvard.edu</a>), cc’ing Robert Barsky (<a href="mailto:robert.barsky@vanderbilt.edu">robert.barsky@vanderbilt.edu</a>). Videos should be submitted in the MP4 format. Audio files should be in the MP3 format. Ensure that the shortest edge of the submitted media is no less than 2048 pixels. If you want to submit in a different creative format, please contact us with any questions about the submission requirements. When submitting, please specify that the piece is a creative submission.
            </p>
            
            <p>
                All accompanying text documents should be attached as Microsoft Office documents (.docx) files.
            </p>
            
            <h4>Captioning</h4>
            <p>
                All images should be accompanied by a written caption including the artist's name, title, date, and source credit. Captions should be included in the submission materials as a separate Microsoft Office Document (.docx) file.
            </p>
            
            <h4>Permissions for Images</h4>
            <p>
                If your work contains borrowed images or other elements that are not of your own authorship, you must obtain the proper copyright permissions. All necessary permissions must be obtained to use copyrighted imagery, such as photographs, illustrations, tables, or drawings.
            </p>
            
            <p>
                All letters of permission should accompany your manuscript in the initial submission. <i>AmeriQuests</i> will not subsidize the cost of attaining permissions for submitted work.
            </p>
            
            <h4>Collaboration</h4>
            <p>
                If more than one contributor has collaborated on a piece of work, each should have the opportunity to view the work before submission and throughout the editorial process. Please designate one person to handle correspondence, but ensure that all parties remain informed of editorial decisions.
            </p>
            
            <h4>AI/LLM Issues</h4>
            <p>
                No LLM tool can be credited as an author on any submission. Any substantive use of LLM text-based or visual-based tools must be appropriately cited.
            </p>
            
            <h4>Artists' Fees</h4>
            <p>
                Artists of creative work shall not be paid for their work, unless the work is especially commissioned for the Journal under special arrangement and under separate contract.
            </p>
            
            <h3>Contact Information</h3>
            <p>
                Direct any questions via email to:
            </p>
            
            <p><b>Dana McKinney White </b><br />
            <i>Unhoused America</i> Special Edition Editor <br />
            mckinneywhite@gsd.harvard.edu</p>
            
            <p><b>Robert Barsky </b><br />
            <i>AmeriQuests</i> Editor <br />
            robert.barsky@Vanderbilt.edu</p>
        

            <hr />

            <p>
                <b><i>AmeriQuests</i> Editor:</b><br/>
                Robert Barsky<br/>
                <i>Guggenheim Fellow<br/>
                Professor of French, European Studies, and Jewish Studies<br/>
                Professor of Law<br/>
                Vanderbilt University<br/></i>
            </p>
            <p>
                <b><i>Unhoused America</i> Special Edition Editor:</b> <br/>
                Dana McKinney White<br/>
                <i>Assistant Professor of Urban Design<br/>
                Harvard University Graduate School of Design</i>
            </p>
            <p>
                <b>Research Assistants:</b><br/>
                Zach Deocadiz, MDes ’26<br/>
                Jazzlynn Derrick, MDes ‘25<br/>
                Tyler White, MDes/MUP ’26<br/>
                Joshua Udemba, MAUD ‘26
            </p>
        </div>
    );
}
export default Home;